/* eslint-disable no-param-reassign */
import type {Dispatch, RefObject, SetStateAction} from 'react';

import {keyframes} from '@emotion/react';
import styled from '@emotion/styled';
import * as motion from 'motion/react-m';

import {ModalEnterExitAnimation} from 'UI/animations/modals';

import {useModalAnimation} from './useModalAnimation';
import {transient} from 'Application/configs/emotionTransientProps';

import type {WithChildren} from 'types/global';

/**
 * The `modalFactory` function is a factory function that generates a `Modal` component.
 * This modal can be customized with size and testId props, handles opening and closing actions,
 * and provides a close button to dismiss the modal. It uses animation for enter and exit transitions.
 *
 * @param state The reference to the state that controls the visibility of the modal.
 * @returns A `Modal` component that renders a modal window with customizable size and content.
 *
 * @example
 * ```tsx
 * const Modal = modalFactory(useState(false)[1]);
 * <Modal size="small" testId="myModal">
 *   <p>Modal content</p>
 * </Modal>
 * ```
 */
const modalFactory = (state: RefObject<Dispatch<SetStateAction<boolean>> | null>) => {
    /**
     * Interface for the props used by the `Modal` component.
     * This component provides a customizable modal dialog, which can be resized and tested using a unique `testId`.
     */
    interface ComponentProps {
        /**
         * Defines the size of the modal. It can either be 'default' or 'small'.
         * The 'default' size is used by default unless otherwise specified.
         * This prop determines the visual size of the modal dialog on the screen.
         *
         * @default 'default'
         */
        size?: 'default' | 'small';
        /**
         * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
         * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
         *
         * @default 'Modal'
         */
        testId?: string;
    }

    /**
     * The `Modal` component renders a modal window that can contain various child elements and handles opening and closing actions.
     * It uses animations for entering and exiting the modal, and provides a close button to dismiss the modal.
     *
     * @param props          The component props.
     * @param props.children The content to be displayed inside the modal.
     * @param props.size     The size of the modal window. Can be `default` or other preset sizes.
     * @param props.testId   The `testId` property represents a unique identifier for testing purposes.
     * @returns A JSX element representing the modal window with content and close functionality.
     *
     * @example
     * ```tsx
     * <Modal size="large" testId="custom-modal">
     *   <p>Modal content goes here.</p>
     * </Modal>
     * ```
     */
    const Modal = ({children, size = 'default', testId = 'Modal'}: WithChildren<ComponentProps>) => {
        const {controls, modal, showContent} = useModalAnimation(state);

        return (
            <Wrapper
                ref={modal}
                $size={size}
                animate={controls}
                data-cy={testId}
                variants={ModalEnterExitAnimation}
            >
                {showContent && children}
            </Wrapper>
        );
    };

    Modal.displayName = 'Modal';
    Modal.defaultProps = {
        size: 'default',
        testId: undefined
    };

    return Modal;
};

export {modalFactory};

const fadeIn = keyframes`
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
`;

interface WrapperProps {
    $size: 'default' | 'small';
}

const Wrapper = styled(motion.dialog, transient)<WrapperProps>`
    align-content: stretch;
    background: ${({theme}) => theme.colors.backgroundLight};
    border: none;
    border-radius: 16px;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
    height: max-content;
    max-height: 86%;
    max-width: 66rem;
    min-height: 20rem;
    opacity: 0;
    outline: none;
    overflow: hidden;
    padding: 0;
    transform: translateY(80px);
    width: 95%;

    &::backdrop {
        animation: ${fadeIn} 0.3s ease-in-out backwards;
        animation-direction: reverse;
        background-color: rgba(0, 0, 0, 0.7);
    }

    &[open]::backdrop {
        animation: ${fadeIn} 0.3s ease-in-out forwards;
    }
`;